import { analytics } from '@/common/analytics/analytics'
import { APP_ROUTES, SWIFTID_PATHS } from '@/common/constants'
import { SWIFTID_KYC_DOMAIN } from '@/common/env'
import { Account, KYCStatus, User } from '@/common/types'
import { Button } from '@/common/ui/button'
import { H2 } from '@/common/ui/h2'
import { KycProviders } from '@/kyc/types'
import { ACCOUNT_TYPE_LABELS, APPLICATION_COPY } from '@/locales/en'
import { CircleCheck, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { ApplicationRedirectPage } from './RedirectModal'

export const SwiftIdResumeContent = ({
    user,
    kyc,
    account,
}: {
    user: User
    kyc: KYCStatus
    account: Account | null
}) => {
    const navigate = useNavigate()
    const [openRedirect, setOpenRedirect] = useState<boolean>(false)

    const [isPinCopied, setIsPinCopied] = useState(false)
    // Ensure correct typing when indexing ACCOUNT_TYPE_LABELS
    const accountTypeKey = (account?.accountType ?? user?.accountType) as keyof typeof ACCOUNT_TYPE_LABELS | undefined
    const accountTypeLabel = accountTypeKey ? ACCOUNT_TYPE_LABELS[accountTypeKey] : 'Individual'

    const goToKYC = () => {
        analytics.trackWithDefaults('application_resumed', {
            resume_reason: 'resumed from menu',
            account_type: account?.accountType ?? user?.accountType,
        })

        if (kyc?.provider === KycProviders.FRANKIEONE) {
            navigate(APP_ROUTES.APPLICATION_KYC)
            return
        }
        setOpenRedirect(true)
        window.location.href = new URL(
            SWIFTID_PATHS.RESUME_PATH.replace('{id}', encodeURIComponent(kyc.kycProviderEntityId || '')),
            SWIFTID_KYC_DOMAIN,
        ).toString()
    }

    useEffect(() => {
        if (!isPinCopied) return
        toast.message(
            <div className="self-stretch inline-flex justify-start items-center gap-6">
                <CircleCheck />
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                    <div className="font-medium text-base">PIN copied!</div>
                </div>
            </div>,
        )
        analytics.trackWithDefaults('pin_copied')
        setIsPinCopied(false)
    }, [isPinCopied])

    return (
        <div className="w-full flex flex-col justify-start items-start gap-6">
            <H2 className="justify-start mb-0">{APPLICATION_COPY.resume.title}</H2>
            <p>
                {APPLICATION_COPY.resume.description
                    .replace('{firstName}', user?.firstName || '')
                    .replace('{lastName}', user?.lastName || '')
                    .replace('{type}', accountTypeLabel)}
            </p>
            <div className="w-full flex flex-col justify-start items-start gap-4">
                <h4 className="text-base font-semibold font-['Inter']">{APPLICATION_COPY.resume.stepOne.title}</h4>
                <div className="inline-flex justify-start items-center gap-2">
                    <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-tight">
                        {APPLICATION_COPY.resume.stepOne.pin}
                    </div>
                    <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-tight">
                        {kyc?.pin || 'N/A'}
                    </div>
                    <CopyToClipboard text={kyc?.pin || 'N/A'} onCopy={() => setIsPinCopied(true)}>
                        <Button variant={'ghost'} className="mx-2 px-2">
                            <Copy />
                        </Button>
                    </CopyToClipboard>
                </div>

                <p className="text-sm">{APPLICATION_COPY.resume.stepOne.description}</p>
            </div>
            <div className="inline-flex flex-col justify-start items-start gap-6">
                <h4 className="text-base font-semibold font-['Inter']">{APPLICATION_COPY.resume.stepTwo.title}</h4>
                <Button variant="secondary" className="h-10 px-6 border-0" onClick={goToKYC}>
                    {APPLICATION_COPY.resume.stepTwo.button}
                </Button>
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <div className="text-sm font-normal font-['Inter'] leading-tight">
                        {APPLICATION_COPY.resume.stepTwo.description}
                    </div>
                </div>
            </div>
            <p
                className="text-sm"
                dangerouslySetInnerHTML={{
                    __html: APPLICATION_COPY.resume.support.replace(
                        '{email}',
                        '<a href="mailto:support@six-invest.com.au" class="text-black underline">support@six-invest.com.au</a>',
                    ),
                }}
            />
            <ApplicationRedirectPage enabled={openRedirect} />
        </div>
    )
}
